import ConsultingHero from "../shared/ConsultingHero/ConsultingHero";
import MascotIntroSection from "../shared/MascotIntroSection/MascotIntroSection";
import ImageContentSection from "../shared/ImageContentSection/ImageContentSection";
import ServiceCardsSection from "../shared/ServiceCardsSection/ServiceCardsSection";
import ProcessDiagram from "../shared/ProcessDiagram/ProcessDiagram";
import LinkButton from "../shared/Buttons/LinkButton";
import ImpactStatsSection from "../shared/ImpactStatsSection/ImpactStatsSection";
import CTABanner from "../shared/CTABanner/CTABanner";
import AssociatedServices from "../shared/AssociatedServices/AssociatedServices";
import FAQ from "../shared/FAQ/FAQ";
import { seoWhyItMatters, seoHowWeHelp, seoImpactStats, seoAssociatedServices, seoFaqs } from "./data/consultingData";

const SEOService = () => {
  return (
    <div>
      <ConsultingHero
        title={
          <>
            <span className="block">Helping Animal</span>
            <span className="block">Rescues Get</span>
            <span className="block">Found Online</span>
          </>
        }
        buttonText="Request a Free SEO Audit"
        buttonTo="/contact-us"
        bgColor="bg-blue"
        imageSrc="https://sap-app-prod.s3.us-west-2.amazonaws.com/consulting-services/consulting-pages-v2/cat-looking-away-480.webp"
        imageSrcSet="
          https://sap-app-prod.s3.us-west-2.amazonaws.com/consulting-services/consulting-pages-v2/cat-looking-away-480.webp 480w,
          https://sap-app-prod.s3.us-west-2.amazonaws.com/consulting-services/consulting-pages-v2/cat-looking-away-1280.webp 1280w,
          https://sap-app-prod.s3.us-west-2.amazonaws.com/consulting-services/consulting-pages-v2/cat-looking-away-1920.webp 1920w
        "
        imageAlt="A cat looking away"
      />
      <MascotIntroSection
        mascotSrc="https://sap-app-prod.s3.us-west-2.amazonaws.com/consulting-services/consulting-pages-v2/starkski-seo.webp"
        mascotAlt="Starski mascot with a magnifying glass"
        circleBg="bg-blue/30"
        title="We help animal nonprofits rank higher on Google & Maps"
        description="Our SEO strategies make it easier for adopters, donors, & volunteers to find you first — so your mission gets more visibility both online & in your community."
      />
      <ImageContentSection
        imagePosition="right"
        imageSrc={seoWhyItMatters.imageSrc}
        imageAlt={seoWhyItMatters.imageAlt}
        title={seoWhyItMatters.title}
        description={seoWhyItMatters.description}
        items={seoWhyItMatters.items}
      />
      <ServiceCardsSection
        bgColor={seoHowWeHelp.bgColor}
        title={seoHowWeHelp.title}
        titleColor={seoHowWeHelp.titleColor}
        accentColor={seoHowWeHelp.accentColor}
        description={seoHowWeHelp.description}
        cardBg={seoHowWeHelp.cardBg}
        items={seoHowWeHelp.items}
      />
      <ProcessDiagram
        title="From audit to action — here's how we work"
        description="Our SEO consulting helps your rescue appear where supporters are already looking — on Google Maps, in local search results, and inside your community's Google Business listings."
        svgSrc="https://sap-app-prod.s3.us-west-2.amazonaws.com/consulting-services/consulting-pages-v2/timeline2.svg"
        svgAlt="SEO process diagram"
      />

      {/* Google Maps ranking section */}
      <section className="px-4 md:px-8 lg:px-16 xl:px-24 my-16 lg:my-24 xl:my-52">
        <div className="max-w-7xl mx-auto flex flex-col md:flex-row items-center gap-12 md:gap-16">

          {/* Left: map image with mascot overlay */}
          <div className="md:w-1/2 relative">
            <img
              src="https://sap-app-prod.s3.us-west-2.amazonaws.com/consulting-services/consulting-pages-v2/map.webp"
              alt="Google Maps ranking dashboard"
              className="w-full rounded-2xl"
              loading="lazy"
            />
            <img
              src="https://sap-app-prod.s3.us-west-2.amazonaws.com/consulting-services/consulting-pages-v2/starski-phone.webp"
              alt="Starski mascot holding a phone"
              className="absolute bottom-0 left-1/2 -translate-x-1/2 w-32 md:w-40 lg:w-48 xl:w-56"
              loading="lazy"
            />
          </div>

          {/* Right: text content */}
          <div className="md:w-1/2 text-center md:text-left">
            <h2 className="text-3xl md:text-4xl lg:text-5xl xl:text-6xl font-bold text-blue-dark1 mb-6">
              Curious Where You Rank on Google Maps
            </h2>
            <p className="!text-sm md:!text-base xl:!text-xl leading-relaxed mb-8 max-w-lg mx-auto md:mx-0">
              We'll scan your rescue's visibility across Google Maps and send you a free ranking heat map and SEO report — no strings attached.
            </p>
            <LinkButton
              to="/contact-us"
              colour="orange"
              className="text-sm md:text-base xl:text-lg md:px-4 md:py-2 xl:px-6 xl:py-3"
            >
              Check my ranking for free
            </LinkButton>
          </div>
        </div>
      </section>
      {/* Why Rescues Trust Sponsor a Pet section */}
      <section className="px-4 md:px-8 lg:px-16 xl:px-24 my-16 lg:my-24 xl:my-52 relative">

        <img
          src="https://sap-app-prod.s3.us-west-2.amazonaws.com/consulting-services/consulting-pages-v2/seo-circles.svg"
          alt=""
          className="absolute hidden lg:block lg:-top-[200px] lg:-right-72 xl:-top-[400px] xl:w-[90%] xl:-right-72 pointer-events-none z-0"
        />

        <div className="max-w-7xl mx-auto flex flex-col md:flex-row items-center gap-12 md:gap-16">

          {/* Left: text content */}
          <div className="md:w-1/2 text-center md:text-left z-10">
            <img
              src="https://sap-app-prod.s3.us-west-2.amazonaws.com/consulting-services/consulting-pages-v2/horizontal-line.svg"
              alt=""
              className="w-28 md:w-40 mb-6 mx-auto md:mx-0"
            />
            <h2 className="text-3xl md:text-4xl lg:text-5xl xl:text-6xl font-bold text-blue-dark1 mb-6">
              Why Rescues Trust Sponsor a Pet
            </h2>
            <p className="!text-sm md:!text-base xl:!text-xl leading-relaxed mb-10 max-w-lg mx-auto md:mx-0">
              Unlike traditional agencies, we specialize exclusively in animal welfare marketing. We understand the adoption journey, donor motivations, and how to turn search traffic into real-world impact.
            </p>

            <div className="flex flex-col gap-6">
              {[
                { icon: "https://sap-app-prod.s3.us-west-2.amazonaws.com/consulting-services/consulting-pages-v2/paw.svg", heading: "Animal rescue expertise", text: "300+ rescue partners worldwide" },
                { icon: "https://sap-app-prod.s3.us-west-2.amazonaws.com/consulting-services/consulting-pages-v2/eye.svg", heading: "Proven SEO strategies", text: "Focused on increasing adoptions, volunteer sign-ups, and donations." },
                { icon: "https://sap-app-prod.s3.us-west-2.amazonaws.com/consulting-services/consulting-pages-v2/message.svg", heading: "Full transparency", text: "Know exactly how your SEO efforts are paying off." },
                { icon: "https://sap-app-prod.s3.us-west-2.amazonaws.com/consulting-services/consulting-pages-v2/up.svg", heading: "Long-term support", text: "We're here to grow with you, not just for a one-time project." },
              ].map((item, index) => (
                <div key={index} className="flex gap-4 items-start justify-center md:justify-start">
                  <div className="w-10 h-10 min-w-[40px] rounded-[50%_50%_5px_50%] bg-orange flex items-center justify-center mt-1">
                    <img src={item.icon} alt="" className="w-5 h-5" />
                  </div>
                  <div className="text-left">
                    <h3 className="font-bold text-blue-dark1 text-base md:text-lg xl:text-xl mb-1">{item.heading}</h3>
                    <p className="!text-xs md:!text-sm xl:!text-base leading-relaxed">{item.text}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Right: cat image */}
          <div className="md:w-1/2 flex items-center justify-center py-8 overflow-visible">
            <div className="relative w-[280px] md:w-[340px] lg:w-[420px] xl:w-[516px]">
              <div className="relative z-10 w-full h-[420px] md:h-[500px] lg:h-[630px] xl:h-[800px] rounded-2xl overflow-hidden">
                <img
                  src="https://sap-app-prod.s3.us-west-2.amazonaws.com/consulting-services/consulting-pages-v2/tabby-kitten.webp"
                  alt="A tabby kitten"
                  className="absolute inset-0 w-full h-full object-cover object-right"
                  loading="lazy"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      <ImpactStatsSection
        bgColor={seoImpactStats.bgColor}
        title={seoImpactStats.title}
        description={seoImpactStats.description}
        image1Src={seoImpactStats.image1Src}
        image1Alt={seoImpactStats.image1Alt}
        image2Src={seoImpactStats.image2Src}
        image2Alt={seoImpactStats.image2Alt}
        stats={seoImpactStats.stats}
        buttonText={seoImpactStats.buttonText}
        buttonTo={seoImpactStats.buttonTo}
        buttonColour={seoImpactStats.buttonColour}
      />

      <CTABanner
        bgColor="bg-blue"
        overlayFrom="from-blue via-blue/80"
        imageSrc="https://sap-app-prod.s3.us-west-2.amazonaws.com/consulting-services/consulting-pages-v2/cat2.webp"
        imageAlt="A tabby kitten"
        imageClassName="object-right translate-x-44"
        title="Ready to get your rescue on the map?"
        description="Let's uncover where you stand, where you can grow, and how we'll get you ranking higher — for free."
        buttonText="Get My Local Ranking Free"
        buttonTo="/contact-us"
        buttonColour="orange"
      />

      <AssociatedServices
        subtitle="More ways to grow your audience."
        services={seoAssociatedServices}
        cols={2}
      />

      <FAQ subtitle="About our SEO services" faqs={seoFaqs} />
    </div>
  );
};

export default SEOService;
